import { useEffect, useMemo, useState } from 'react'
import type { Edge } from '@xyflow/react'
import { Button } from 'app/components/ui/button'
import { useListCopy } from 'app/hooks/useListCopy'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { notify, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { logError } from 'lib/logging'
import { ContactSearchField } from '../ContactSearchField'
import { CONTACT_FIELDS, composableTestFields } from 'app/lib/workflows/dataContext'
import { buildPreview } from 'app/lib/workflows/preview'
import { triggerObject } from 'app/lib/workflows/triggerCatalog'
import type { TriggerNodeData, WorkflowNode } from 'app/types/workflows'
import { useTriggerSamples } from '../useTriggerSamples'
import { Alert } from './Alert'
import { Section } from './Section'

type TriggerTestProps = {
  data: TriggerNodeData
  /** Workflow is active on the server — else the engine won't enroll a run into it. */
  active?: boolean
  /** Workflow has been saved to the server (has a remoteId). */
  saved?: boolean
  /** Unsaved changes: a fire would run the last-saved version, so block it until saved. */
  dirty?: boolean
  /** The full graph, for the non-destructive dry-run preview. */
  nodes?: WorkflowNode[]
  edges?: Edge[]
}

const EMPTY_NODES: WorkflowNode[] = []
const EMPTY_EDGES: Edge[] = []

/** The activity type a test would fabricate for this trigger (Call → Call, Form → Form),
 *  or undefined if the trigger isn't activity-sourced (Contact/Appointment/Task). Also gates
 *  whether a "Test" tab is worth showing. */
export function triggerTestType(data: TriggerNodeData): string | undefined {
  const object = triggerObject(data.kind)
  if (object === `call`) return `Call`
  if (object === `form`) return `Form`
  return undefined
}

/** Super-only "Test this trigger": compose a sample of THIS trigger's own data and fire it for real,
 *  so the trigger itself runs (matching → enrollment → steps). Lives on the trigger because the test IS
 *  a sample of the trigger — its fields are the inputs, no re-derivation. Only for activity-sourced
 *  triggers (Form/Call); Contact triggers have no fabricated activity. Contact ID is still required for
 *  now (it sources org/workspace/location); the contactless "matching" mode waits on the endpoint
 *  routing through real ingestion (see TRK-5299). */
export function TriggerTest({
  data,
  active,
  saved,
  dirty,
  nodes = EMPTY_NODES,
  edges = EMPTY_EDGES,
}: TriggerTestProps) {
  const isSuper = useUser()?.type === `super`
  const testActivityType = triggerTestType(data)
  const fields = composableTestFields(data)
  const { samples, loading: samplesLoading } = useTriggerSamples(data)
  const copy = useListCopy()
  const [contactId, setContactId] = useState(``)
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [contactFields, setContactFields] = useState<Record<string, string>>({})
  const [firing, setFiring] = useState(false)

  // Resolve the chosen contact's real fields so {{firstName}}/{{cellPhone}}/… fill in the preview too
  // (the compose/sample fields only cover the activity + form). Read-only.
  useEffect(() => {
    const id = contactId.trim()
    if (id.length < 12) {
      setContactFields({})
      return
    }
    let alive = true
    gqlFetchOne<Record<string, unknown>>(`contact`, id)
      .then((contact) => {
        if (!alive || !contact) return
        const resolved: Record<string, string> = {}
        for (const { key } of CONTACT_FIELDS) {
          const value = contact[key]
          if (value == null || value === ``) continue
          resolved[key] =
            key === `leadOwner` && typeof value === `object`
              ? String((value as { name?: string }).name ?? ``)
              : String(value)
        }
        setContactFields(resolved)
      })
      .catch(() => setContactFields({}))
    return () => {
      alive = false
    }
  }, [contactId])

  // Non-destructive dry-run: what this sample (contact + composed fields) would do at each step.
  const sample = useMemo(() => ({ ...contactFields, ...fieldValues }), [contactFields, fieldValues])
  const preview = useMemo(() => buildPreview(nodes, edges, sample, copy), [nodes, edges, sample, copy])
  const hasSample = Object.values(sample).some((v) => v.trim())

  if (!isSuper || !testActivityType) return null

  // "Use a recent record": prefill the sample fields + contact from a real recent activity.
  const useSample = (id: string) => {
    const sample = samples.find((s) => s.id === id)
    if (!sample) return
    setFieldValues(sample.fields)
    if (sample.contactId) setContactId(sample.contactId)
  }

  // The composed sample → the activity's `data`. `answers.<name>` fields nest under `answers`; the rest
  // go top-level, matching the FormJson shape the engine/timeline expect.
  const buildData = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {}
    const answers: Record<string, string> = {}
    for (const { key } of fields) {
      const value = fieldValues[key]?.trim()
      if (!value) continue
      if (key.startsWith(`answers.`)) answers[key.slice(`answers.`.length)] = value
      else out[key] = value
    }
    if (Object.keys(answers).length) out.answers = answers
    return out
  }

  const onFire = async () => {
    if (!contactId.trim()) return
    setFiring(true)
    try {
      const contact = await gqlFetchOne<{ orgId?: string; workspaceId?: string; locationId?: string }>(
        `contact`,
        contactId.trim(),
      )
      if (!contact?.orgId || !contact?.workspaceId || !contact?.locationId) {
        notify(`Couldn't read that contact's org / workspace / location. Check the Contact ID.`)
        return
      }
      const res = await window.fetch(`/api/testing-activities`, {
        method: `POST`,
        credentials: `include`,
        headers: { 'Content-Type': `application/json` },
        body: JSON.stringify({
          type: testActivityType,
          orgId: contact.orgId,
          workspaceId: contact.workspaceId,
          locationId: contact.locationId,
          contactId: contactId.trim(),
          data: buildData(),
        }),
      })
      const json = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string }
      if (!res.ok || json.success === false || json.error) {
        notify(`Couldn't create test ${testActivityType}: ${json.error ?? res.statusText}`)
      } else {
        notifySuccess(`Created a ${testActivityType}. Open Runs to watch it if it matches.`)
        setFieldValues({})
      }
    } catch (err) {
      logError(`Test trigger fire failed`, err)
      notify(`Couldn't create the test activity.`)
    } finally {
      setFiring(false)
    }
  }

  const notice = !saved
    ? `Save this workflow first, then you can fire a test.`
    : dirty
      ? `You have unsaved changes. Save first, or the test would run the last-saved version.`
      : !active
        ? `This workflow is a draft. Set it live (top bar) so a test run actually enrolls.`
        : `Heads up: this creates a real ${testActivityType} and runs the workflow for real (live messages).`

  return (
    <div className="space-y-3">
      <p className="text-[11px] font-semibold text-default">Test this trigger</p>
      <Alert tone="warn">{notice}</Alert>

      <Section title="Test record">
        {/* Use a recent record — pull real data instead of typing (read-only; creates nothing). */}
        {(samples.length > 0 || samplesLoading) && (
          <div>
            <label htmlFor="tt-sample" className="block text-[10px] text-subtle">
              Use a recent {testActivityType.toLowerCase()}
            </label>
            <select
              id="tt-sample"
              defaultValue=""
              disabled={samplesLoading && samples.length === 0}
              onChange={(e) => useSample(e.target.value)}
              className="w-full rounded-md border border-strong bg-surface px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
            >
              <option value="">{samplesLoading ? `Loading recent records…` : `Pick a recent one to prefill…`}</option>
              {samples.map((sample) => (
                <option key={sample.id} value={sample.id}>
                  {sample.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor="tt-contact" className="block text-[10px] text-subtle">
            Contact to test on
          </label>
          {/* Search-by-name, never a raw id (UX audit C4). "Use a recent record" can still set the
              id programmatically; the field resolves the name itself. */}
          <ContactSearchField inputId="tt-contact" value={contactId} onChange={(id) => setContactId(id)} />
        </div>
      </Section>

      {fields.length > 0 && (
        <Section title="Sample data">
          <div className="space-y-1">
            <p className="text-[10px] font-medium text-subtle">Sample {testActivityType.toLowerCase()} data</p>
            {fields.map((field) => (
              <div key={field.key} className="flex items-center gap-2">
                <label
                  htmlFor={`tt-${field.key}`}
                  className="w-24 shrink-0 truncate text-[10px] text-subtle"
                  title={field.label}
                >
                  {field.label}
                </label>
                <input
                  id={`tt-${field.key}`}
                  value={fieldValues[field.key] ?? ``}
                  onChange={(e) => setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.label}
                  className="min-w-0 flex-1 rounded-md border border-strong px-2 py-1 text-[11px] focus:border-blue-500 focus:outline-none"
                />
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Non-destructive dry-run: what this sample would do at each step (variables filled, branches
          decided). Creates nothing — this is the safe way to iterate. */}
      {hasSample && preview.length > 0 && (
        <Section title="Preview">
          <div className="space-y-1">
            <p className="text-[10px] font-medium text-subtle">Preview: what this sample would do</p>
            <ol className="space-y-1">
              {preview.map((step, i) => (
                <li
                  key={step.id}
                  className="rounded-md border border-default bg-surface px-2 py-1 text-[10px] text-default"
                >
                  <span className="text-minimal">{i + 1}.</span> {step.detail}
                  {step.note && <span className="mt-0.5 block text-[9px] text-minimal">{step.note}</span>}
                </li>
              ))}
            </ol>
          </div>
        </Section>
      )}

      <Button
        variant="white"
        size="sm"
        disabled={!active || !saved || dirty || !contactId.trim() || firing}
        onClick={onFire}
      >
        {firing ? `Firing…` : `Fire test ${testActivityType.toLowerCase()}`}
      </Button>
      <p className="text-[10px] leading-relaxed text-minimal">
        Preview creates nothing. “Fire” runs it for real (super-user only).
      </p>
    </div>
  )
}
